import type { ReactElement } from "react";
import {
  Button,
  CloudLockIcon,
  LetterIcon,
  LockIcon,
  Text,
} from "@olivahealth/oli-ui";
import tokens from "@olivahealth/oli-ui/theme/tokens";
import { NexusGenObjects } from "@olivahealth/graphql-server/src/ui/types/graphql.gen";
import MultiFactorForm from "../MultiFactorForm";
import { CenteredInfinityLoader } from "../../../molecules/Loaders/InfinityLoader";
import { AuthMethod } from "../AuthPanel/types";
import useTranslation from "../../../../hooks/useTranslation";
import useSocialSignUp, { ProviderButtons } from "./useSocialSignUp";

interface Props {
  invitation?: Pick<NexusGenObjects["Invitation"], "id" | "email"> & {
    organisation?: {
      isWorkOnlyOrganisation?: boolean;
    } | null;
  };
  isLogin?: boolean;
  toggleAuthMethod: (authMethod: AuthMethod) => () => void;
  from?: string;
}

export default function SocialSignUpForm({
  invitation,
  isLogin = false,
  toggleAuthMethod,
  from,
}: Props): ReactElement {
  const {
    data: {
      isMfaRoute,
      multifactorStarted,
      multifactorLoading,
      formInputProps,
      formError,
    },
    effects: { onFormSubmit, openSocialSignUp },
  } = useSocialSignUp({
    invitation,
    isLogin,
    from,
  });
  const { t, Trans } = useTranslation("auth");
  const hasInvitation = Boolean(invitation);

  return (
    <>
      {multifactorStarted ? (
        <MultiFactorForm
          onSubmit={onFormSubmit}
          register={formInputProps.register}
        />
      ) : (
        <>
          {isMfaRoute ? (
            <div className="flex flex-col items-center gap-2 mb-6">
              <LockIcon color={tokens.colors.purple[500]} size={48} />
              <Text weight="bold" align="center" size="xl" color="white">
                {t("multiFactor.activateTitle")}
              </Text>

              <Text align="center" color="white">
                {t("multiFactor.activateSignInMessage")}
              </Text>
            </div>
          ) : (
            <Text
              size="5xl"
              weight="bold"
              align="center"
              fontFamily="centra"
              gutter={hasInvitation ? "md" : "none"}
              color="white"
            >
              {multifactorLoading
                ? t("multiFactor.title")
                : isLogin
                  ? t("socialSignUpForm.titleLogin")
                  : invitation?.organisation?.isWorkOnlyOrganisation
                    ? t("socialSignUpForm.titleWorkSignup")
                    : t("socialSignUpForm.titleSignup")}
            </Text>
          )}
          {!multifactorLoading ? (
            <>
              {hasInvitation && (
                <Text color="white">
                  <Trans
                    i18nKey={t("socialSignUpForm.invitationMessage")}
                    values={{ email: invitation?.email }}
                    components={[<span key="bold" className="font-bold" />]}
                  />
                </Text>
              )}
              <div className="flex w-full flex-col space-y-3 mt-8">
                {ProviderButtons.map(({ provider, id, logo }) => {
                  return (
                    <Button
                      key={id}
                      iconLeft={logo}
                      variant="label"
                      width="full"
                      onClick={() => openSocialSignUp(provider)}
                    >
                      {t(
                        `socialSignUpForm.providers.${id.toLowerCase()}.${
                          isLogin ? "login" : "signup"
                        }`,
                      )}
                    </Button>
                  );
                })}

                <Button
                  iconLeft={<LetterIcon color={tokens.colors["purple"][300]} />}
                  variant="label"
                  onClick={toggleAuthMethod("email")}
                >
                  {t(
                    `socialSignUpForm.providers.email.${
                      isLogin ? "login" : "signup"
                    }`,
                  )}
                </Button>

                <Button
                  iconLeft={
                    <CloudLockIcon color={tokens.colors["purple"][300]} />
                  }
                  variant="label"
                  onClick={toggleAuthMethod("sso")}
                >
                  {t(
                    `socialSignUpForm.providers.sso.${
                      isLogin ? "login" : "signup"
                    }`,
                  )}
                </Button>
              </div>
            </>
          ) : (
            <CenteredInfinityLoader height="full" />
          )}
        </>
      )}

      {formError && <p className="font-gray-600 font-sans">{formError}</p>}
      <div id="social-recaptcha-container"></div>
    </>
  );
}
